import { useState, useEffect, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Upload, CheckCircle, Loader2, Eye, EyeOff } from 'lucide-react'
import { cn } from '@lib/utils/helpers'
import { useAppDispatch, useAppSelector } from '@/app/hooks'
import {
  fetchApplications,
  fetchApplicationById,
  selectApplicationData,
  selectApplications,
  selectApplicationLoading,
  selectApplicationError,
  selectIsReadOnly,
  clearError,
} from '@/features/applicant/applicationSlice'
import { logoutAndRedirect } from '@/lib/api/authService'
import { logout } from '@/features/auth/authSlice'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { ValidationModal } from '@/components/ui/ValidationModal'
import {
  applicantConfirmDialogProps,
  applicantValidationModalProps,
} from '@/features/applicant/constants/applicantDialogProps'
import { updateProfile, verifyUser, changePassword } from '@/lib/api/userService'
import { formatPhoneNumber, unformatPhoneNumber } from '@/types/application'
import apiClient from '@/lib/api/apiClient'
import { getStorageItem, STORAGE_KEYS } from '@/lib/utils/storage'
import { API_ENDPOINTS } from '@/lib/api/config'
import { trackApplicationEvent } from '@/lib/utils/analytics'
import { StopImpersonateButton } from '@/components/ui/StopImpersonateButton'
import type { PasswordFormData, ProfileFormData } from '@/features/applicant/types/profile'

/**
 * Name validation pattern from API documentation
 * Pattern: /^(?! )[a-zA-Z0-9!@#$%^&*()_+=[\]{}|;:\'",.<>?/`~\\-]+(?: [a-zA-Z0-9!@#$%^&*()_+=[\]{}|;:\'",.<>?/`~\\-]+){0,2}(?<! )$/
 */
const namePattern = /^(?! )[a-zA-Z0-9!@#$%^&*()_+=[\]{}|;:'",.<>?/`~\\-]+(?: [a-zA-Z0-9!@#$%^&*()_+=[\]{}|;:'",.<>?/`~\\-]+){0,2}(?<! )$/

/**
 * Validation schema for profile form
 */
const profileSchema = z.object({
  firstName: z
    .string()
    .min(1, 'First Name is required.')
    .regex(namePattern, 'Enter Valid First Name'),
  lastName: z
    .string()
    .min(1, 'Last Name is required.')
    .regex(namePattern, 'Enter Valid Last Name'),
  mobileNo: z
    .string()
    .min(1, 'Contact No is required.')
    .refine((val) => {
      const digits = val.replace(/\D/g, '')
      return digits.length === 10 || digits.length === 12 || digits.length === 13
    }, 'Enter Valid Contact Phone (10, 12, or 13 digits). Examples: (123) 456-7890 or +12(345) 678-9012'),
  email: z
    .string()
    .optional()
    .refine((val) => !val || z.string().email().safeParse(val).success, {
      message: 'Email is not valid.',
    }),
})

/**
 * Validation schema for password form
 */
const passwordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Current password is required'),
    newPassword: z
      .string()
      .min(1, 'New password is required')
      .min(6, 'Password must be at least 6 characters'),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })

export function Profile() {
  const dispatch = useAppDispatch()

  const [activeTab, setActiveTab] = useState<'account' | 'password'>('account')
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [localError, setLocalError] = useState<string | null>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [showPasswordChangeDialog, setShowPasswordChangeDialog] = useState(false)
  const [redirectCountdown, setRedirectCountdown] = useState<number>(5)
  const [isLoggingOut, setIsLoggingOut] = useState(false)
  const redirectIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Redux state
  const applicationData = useAppSelector(selectApplicationData)
  const applications = useAppSelector(selectApplications)
  const isLoading = useAppSelector(selectApplicationLoading)
  const error = useAppSelector(selectApplicationError)
  const isReadOnly = useAppSelector(selectIsReadOnly)

  // Profile form
  const {
    register: registerProfile,
    handleSubmit: handleSubmitProfile,
    reset: resetProfile,
    watch: watchProfile,
    formState: { errors: profileErrors },
  } = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      mobileNo: '',
      email: '',
    },
  })

  // Password form
  const {
    register: registerPassword,
    handleSubmit: handleSubmitPassword,
    reset: resetPassword,
    formState: { errors: passwordErrors, isSubmitting: isSubmittingPassword },
  } = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  })

  // Track if we've already attempted to fetch
  const [hasFetched, setHasFetched] = useState(false)
  const [hasFetchedById, setHasFetchedById] = useState(false)

  // Fetch application data on mount only if not already loaded
  useEffect(() => {
    dispatch(clearError())
    // Only fetch if: no data, not loading, and haven't fetched yet
    if (!applicationData && !isLoading && !hasFetched) {
      setHasFetched(true)
      dispatch(fetchApplications())
    }
  }, [applicationData, isLoading, hasFetched, dispatch])

  // After fetching applications, fetch detailed data by ID if we have applicant_id
  useEffect(() => {
    if (hasFetched && !hasFetchedById && !isLoading && (applicationData || applications.length > 0)) {
      // Get applicant_id from applicationData or first application in array
      const applicantId = applicationData?.applicant_id ||
        (applications.length > 0 ? applications[0]?.applicant_id : null)

      if (applicantId) {
        setHasFetchedById(true)
        dispatch(fetchApplicationById(applicantId)).catch(() => {
          // If fetch by ID fails, continue with existing data
          setHasFetchedById(true) // Mark as fetched to prevent retry loop
        })
      } else {
        // No applicant_id available, mark as fetched to prevent retry
        setHasFetchedById(true)
      }
    }
  }, [hasFetched, hasFetchedById, isLoading, applicationData, applications, dispatch])

  // Populate form with application data
  useEffect(() => {
    if (applicationData) {
      const userData = applicationData.user_data

      resetProfile({
        firstName: userData?.first_name || applicationData.first_name || '',
        lastName: userData?.last_name || applicationData.last_name || '',
        mobileNo: formatPhoneNumber(userData?.mobile_no || applicationData.mobile_no),
        email: userData?.email || applicationData.email || '',
      })
    }
  }, [applicationData, resetProfile])

  /**
   * Filter empty values from data object
   * Removes all empty/null/whitespace-only values
   */
  const getFilterValue = (data: Record<string, unknown>): Record<string, unknown> => {
    const filterParams: Record<string, unknown> = {}
    for (const filterKey in data) {
      if (Object.prototype.hasOwnProperty.call(data, filterKey)) {
        const value = typeof data[filterKey] === 'string'
          ? (data[filterKey] as string).trim()
          : data[filterKey]
        if (value && value !== '') {
          filterParams[filterKey] = value
        }
      }
    }
    return filterParams
  }

  // Handle profile form submit
  const onSubmitProfile = async (data: ProfileFormData) => {
    setSuccessMessage(null)
    setLocalError(null)
    dispatch(clearError())

    try {
      // Process phone number - remove all non-digit characters
      const processedData = {
        first_name: data.firstName.trim(),
        last_name: data.lastName.trim(),
        mobile_no: unformatPhoneNumber(data.mobileNo),
        ...(data.email?.trim() && { email: data.email.trim().toLowerCase() }),
      }

      // Filter empty values
      const filteredData = getFilterValue(processedData) as {
        first_name: string
        last_name: string
        mobile_no: string
        email?: string
      }

      // Call the profile update API
      await updateProfile(filteredData)
      trackApplicationEvent('profile_updated', 'user_management')

      // Refresh user data to get updated profile
      await verifyUser(true)

      // Also refresh application data if available
      if (applicationData?.applicant_id) {
        await dispatch(fetchApplicationById(applicationData.applicant_id)).unwrap()
      }

      setSuccessMessage('Profile Updated Successfully')

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000)
    } catch (err) {
      // Handle error response - can be string or object
      let errorMessage = 'Failed to update profile. Please try again.'

      if (typeof err === 'string') {
        errorMessage = err
      } else if (err && typeof err === 'object') {
        const apiError = err as { message?: string; error?: string | Record<string, string | string[]> }
        if (apiError.message) {
          errorMessage = apiError.message
        } else if (apiError.error) {
          if (typeof apiError.error === 'string') {
            errorMessage = apiError.error
          } else if (typeof apiError.error === 'object') {
            // Handle object with field errors
            const fieldErrors = Object.values(apiError.error)
              .flat()
              .filter((msg): msg is string => typeof msg === 'string')
            errorMessage = fieldErrors.join(', ') || errorMessage
          }
        }
      }

      setLocalError(errorMessage)
    }
  }

  // Handle password form submit
  const onSubmitPassword = async (data: PasswordFormData) => {
    setSuccessMessage(null)
    setLocalError(null)
    dispatch(clearError())

    try {
      // Map form data to API format
      const passwordData = {
        old_password: data.currentPassword,
        new_password: data.newPassword,
        confirm_password: data.confirmPassword,
      }

      // Call the password change API
      await changePassword(passwordData)
      trackApplicationEvent('password_changed', 'user_management')

      // Show success message
      setSuccessMessage('Password Change Successfully')
      resetPassword()

      // Show confirmation dialog and start countdown (handled by useEffect)
      setRedirectCountdown(5)
      setShowPasswordChangeDialog(true)
    } catch (err) {
      // Handle error response - can be string or object
      let errorMessage = 'Failed to change password. Please try again.'

      if (typeof err === 'string') {
        errorMessage = err
      } else if (err && typeof err === 'object') {
        const apiError = err as { message?: string; error?: string | Record<string, string | string[]> }
        if (apiError.message) {
          errorMessage = apiError.message
        } else if (apiError.error) {
          if (typeof apiError.error === 'string') {
            errorMessage = apiError.error
          } else if (typeof apiError.error === 'object') {
            // Handle object with field errors
            const fieldErrors = Object.values(apiError.error)
              .flat()
              .filter((msg): msg is string => typeof msg === 'string')
            errorMessage = fieldErrors.join(', ') || errorMessage
          }
        }
      }

      setLocalError(errorMessage)
    }
  }

  // Handle logout after password change and redirect to Angular logout
  const handlePasswordChangeLogout = async () => {
    // Guard against double-execution (countdown + OK click at the same time)
    if (isLoggingOut) return
    setIsLoggingOut(true)

    if (redirectIntervalRef.current) {
      clearInterval(redirectIntervalRef.current)
      redirectIntervalRef.current = null
    }

    // Clear Redux state immediately to prevent race conditions
    dispatch(logout())
    // Uses logoutAndRedirect() directly (no Redux dispatch) to avoid
    // Avoid ProtectedRoute racing the redirect to /login
    await logoutAndRedirect()
  }

  // Countdown when password change dialog is open
  useEffect(() => {
    if (!showPasswordChangeDialog) return
    redirectIntervalRef.current = setInterval(() => {
      setRedirectCountdown((prev) => (prev <= 1 ? 0 : prev - 1))
    }, 1000)
    return () => {
      if (redirectIntervalRef.current) {
        clearInterval(redirectIntervalRef.current)
        redirectIntervalRef.current = null
      }
    }
  }, [showPasswordChangeDialog])

  // Auto-redirect when countdown reaches 0
  useEffect(() => {
    if (showPasswordChangeDialog && redirectCountdown === 0) {
      if (redirectIntervalRef.current) {
        clearInterval(redirectIntervalRef.current)
        redirectIntervalRef.current = null
      }
      handlePasswordChangeLogout()
    }
  }, [showPasswordChangeDialog, redirectCountdown])

  // Get display name
  const displayName = applicationData
    ? `${applicationData.user_data?.first_name || applicationData.first_name || ''} ${applicationData.user_data?.last_name || applicationData.last_name || ''
      }`.trim()
    : 'User'

  // Get user initials for profile icon
  const getUserInitials = () => {
    if (applicationData) {
      const firstName = applicationData.user_data?.first_name || applicationData.first_name || ''
      const lastName = applicationData.user_data?.last_name || applicationData.last_name || ''

      if (firstName && lastName) {
        return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()
      } else if (firstName) {
        return firstName.charAt(0).toUpperCase()
      } else if (lastName) {
        return lastName.charAt(0).toUpperCase()
      }
    }
    return 'U'
  }

  const userInitials = getUserInitials()

  // Handle file selection
  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      // Validate file type: allow images, PDF, and HEIC/HEIF
      const allowedTypes = file.type.startsWith('image/') ||
        file.type === 'application/pdf' ||
        file.type === 'image/heic' ||
        file.type === 'image/heif'
      const allowedExtensions = /\.(pdf|heic|heif|jpg|jpeg|png|gif|webp|bmp)$/i.test(file.name)
      if (!allowedTypes && !allowedExtensions) {
        setLocalError('Please select an image, PDF, or HEIC file.')
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
        return
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setLocalError('File size must be less than 5MB.')
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
        return
      }

      setSelectedFile(file)
      setLocalError(null)

      // Create preview URL
      const url = URL.createObjectURL(file)
      setPreviewUrl(url)

      // Automatically upload the file after selection
      await handleUploadProfilePicture(file)
    }
  }

  // Handle profile picture upload
  const handleUploadProfilePicture = async (file?: File) => {
    const fileToUpload = file || selectedFile
    if (!fileToUpload) {
      setLocalError('Please select a file to upload.')
      return
    }

    setIsUploading(true)
    setLocalError(null)
    setSuccessMessage(null)

    try {
      const formData = new FormData()
      // Append file - ensure the file object is properly included
      formData.append('profile_picture', fileToUpload, fileToUpload.name)

      // Get token for additional header
      const token = getStorageItem(STORAGE_KEYS.authToken)

      // For FormData uploads, the interceptor will handle Content-Type automatically
      // Make sure axios doesn't transform the FormData
      await apiClient.post(
        API_ENDPOINTS.PROFILE,
        formData,
        {
          headers: {
            'Accept-Language': 'en-US,en;q=0.9',
            ...(token && { token }), // Additional token header as per API docs
          },
          // Ensure FormData is not transformed
          transformRequest: [
            (data) => {
              // Return FormData as-is without transformation
              if (data instanceof FormData) {
                return data
              }
              return data
            },
          ],
        }
      )

      // Refresh user data to get updated profile picture
      await verifyUser(true)

      // Also refresh application data if available
      if (applicationData?.applicant_id) {
        await dispatch(fetchApplicationById(applicationData.applicant_id)).unwrap()
      }

      setSuccessMessage('Profile picture uploaded successfully!')
      trackApplicationEvent('profile_photo_updated', 'user_management')
      setSelectedFile(null)

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }

      // Clean up preview URL
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl)
        setPreviewUrl(null)
      }

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000)
    } catch (err) {
      // Handle error response
      let errorMessage = 'Failed to upload profile picture. Please try again.'

      if (typeof err === 'string') {
        errorMessage = err
      } else if (err && typeof err === 'object') {
        const apiError = err as { message?: string; error?: string | Record<string, string | string[]> }
        if (apiError.message) {
          errorMessage = apiError.message
        } else if (apiError.error) {
          if (typeof apiError.error === 'string') {
            errorMessage = apiError.error
          } else if (typeof apiError.error === 'object') {
            const fieldErrors = Object.values(apiError.error)
              .flat()
              .filter((msg): msg is string => typeof msg === 'string')
            errorMessage = fieldErrors.join(', ') || errorMessage
          }
        }
      }

      setLocalError(errorMessage)
    } finally {
      setIsUploading(false)
    }
  }

  // Clean up preview URL on unmount
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl)
      }
    }
  }, [previewUrl])

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="p-4 sm:p-8 pb-4 sm:pb-6 flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-3xl font-bold text-primary">PROFILE</h1>
        <StopImpersonateButton />
      </div>

      {/* Main Content */}
      <div className="px-4 sm:px-6 md:px-8 pb-6 sm:pb-8">
        {/* Loading State */}
        {isLoading && !applicationData && (
          <div className="bg-white rounded-xl shadow-lg p-4 sm:p-8 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            <span className="ml-3 text-gray-600">Loading profile...</span>
          </div>
        )}

        {/* Success Message */}
        {successMessage && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
            <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
            <p className="text-green-800">{successMessage}</p>
          </div>
        )}

        {/* Profile Card */}
        {(!isLoading || applicationData) && (
          <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 md:p-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Left Side - Profile Picture Section */}
              <div className="lg:col-span-1">
                <div className="flex flex-col items-center">
                  {/* Profile Picture */}
                  <div className="w-48 h-48 rounded-full bg-gray-200 flex items-center justify-center mb-6 border-4 border-gray-300 overflow-hidden">
                    {previewUrl ? (
                      <img
                        src={previewUrl}
                        alt="Profile preview"
                        className="w-full h-full object-cover"
                      />
                    ) : applicationData?.user_data?.profile_picture ? (
                      <img
                        src={applicationData.user_data.profile_picture}
                        alt="Profile"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full rounded-full bg-teal-600 flex items-center justify-center">
                        <span className="text-white text-6xl font-bold">
                          {userInitials}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Name */}
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">
                    {displayName}
                  </h2>

                  {/* Upload Profile Picture */}
                  <div className="w-full">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Upload Profile picture:
                    </label>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*,application/pdf,.pdf,.heic,.heif"
                      className="hidden"
                      id="profile-upload"
                      disabled={isReadOnly || isUploading}
                      onChange={handleFileSelect}
                    />
                    <button
                      type="button"
                      disabled={isReadOnly || isUploading}
                      onClick={() => {
                        if (!isReadOnly && !isUploading) {
                          fileInputRef.current?.click()
                        }
                      }}
                      className={cn(
                        'w-full px-4 py-2 bg-[#1597ff] text-white rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2',
                        isReadOnly || isUploading
                          ? 'opacity-50 cursor-not-allowed'
                          : 'hover:bg-[#0d7acc]'
                      )}
                    >
                      {isUploading ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Uploading...
                        </>
                      ) : selectedFile ? (
                        <>
                          <Upload className="w-4 h-4" />
                          Upload {selectedFile.name}
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4" />
                          Upload
                        </>
                      )}
                    </button>
                    {selectedFile && !isUploading && (
                      <p className="mt-2 text-xs text-gray-500 text-center">
                        Selected: {selectedFile.name}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Right Side - Account Details */}
              <div className="lg:col-span-2">
                {/* Tabs */}
                <div className="flex gap-1 border-b border-gray-200 mb-6">
                  <button
                    onClick={() => setActiveTab('account')}
                    className={`px-6 py-3 font-semibold text-sm transition-colors relative ${activeTab === 'account'
                      ? 'text-[#1597ff]'
                      : 'text-gray-600 hover:text-gray-900'
                      }`}
                  >
                    Account
                    {activeTab === 'account' && (
                      <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#1597ff]"></span>
                    )}
                  </button>
                  <button
                    onClick={() => setActiveTab('password')}
                    className={`px-6 py-3 font-semibold text-sm transition-colors relative ${activeTab === 'password'
                      ? 'text-[#1597ff]'
                      : 'text-gray-600 hover:text-gray-900'
                      }`}
                  >
                    Change Password
                    {activeTab === 'password' && (
                      <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#1597ff]"></span>
                    )}
                  </button>
                </div>

                {/* Form Content */}
                {activeTab === 'account' ? (
                  <form onSubmit={handleSubmitProfile(onSubmitProfile)}>
                    <div className="space-y-6">
                      <div className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {/* First Name */}
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2 uppercase">
                              Firstname<span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              {...registerProfile('firstName')}
                              disabled={true}
                              className={cn(
                                'w-full px-4 py-2.5 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                                profileErrors.firstName
                                  ? 'border-red-500'
                                  : 'border-gray-300',
                                'opacity-50 cursor-not-allowed bg-gray-100'
                              )}
                            />
                            {profileErrors.firstName && (
                              <p className="mt-1 text-sm text-red-600">
                                {profileErrors.firstName.message}
                              </p>
                            )}
                          </div>

                          {/* Last Name */}
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2 uppercase">
                              Lastname<span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              {...registerProfile('lastName')}
                              disabled={true}
                              className={cn(
                                'w-full px-4 py-2.5 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                                profileErrors.lastName
                                  ? 'border-red-500'
                                  : 'border-gray-300',
                                'opacity-50 cursor-not-allowed bg-gray-100'
                              )}
                            />
                            {profileErrors.lastName && (
                              <p className="mt-1 text-sm text-red-600">
                                {profileErrors.lastName.message}
                              </p>
                            )}
                          </div>

                          {/* Contact No */}
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2 uppercase">
                              Contact No<span className="text-red-500">*</span>
                            </label>
                            <input
                              type="tel"
                              disabled={true}
                              placeholder="(123) 456-7890"
                              value={watchProfile('mobileNo') || ''}
                              className={cn(
                                'w-full px-4 py-2.5 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                                profileErrors.mobileNo
                                  ? 'border-red-500'
                                  : 'border-gray-300',
                                'opacity-50 cursor-not-allowed bg-gray-100'
                              )}
                            />
                            {profileErrors.mobileNo && (
                              <p className="mt-1 text-sm text-red-600">
                                {profileErrors.mobileNo.message}
                              </p>
                            )}
                          </div>

                          {/* Email */}
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2 uppercase">
                              Email
                            </label>
                            <input
                              type="email"
                              {...registerProfile('email')}
                              disabled={true}
                              className={cn(
                                'w-full px-4 py-2.5 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                                profileErrors.email
                                  ? 'border-red-500'
                                  : 'border-gray-300',
                                'opacity-50 cursor-not-allowed bg-gray-100'
                              )}
                            />
                            {profileErrors.email && (
                              <p className="mt-1 text-sm text-red-600">
                                {profileErrors.email.message}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </form>
                ) : (
                  <form onSubmit={handleSubmitPassword(onSubmitPassword)}>
                    <div className="space-y-6 max-w-2xl">
                      {/* Current Password */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2 uppercase">
                          Current Password<span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <input
                            type={showCurrentPassword ? 'text' : 'password'}
                            {...registerPassword('currentPassword')}
                            disabled={isSubmittingPassword}
                            className={cn(
                              'w-full px-4 py-2.5 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-12',
                              passwordErrors.currentPassword
                                ? 'border-red-500'
                                : 'border-gray-300',
                              isSubmittingPassword &&
                              'opacity-50 cursor-not-allowed'
                            )}
                          />
                          <button
                            type="button"
                            onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                            disabled={isSubmittingPassword}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 disabled:opacity-50"
                          >
                            {showCurrentPassword ? (
                              <EyeOff className="h-5 w-5" />
                            ) : (
                              <Eye className="h-5 w-5" />
                            )}
                          </button>
                        </div>
                        {passwordErrors.currentPassword && (
                          <p className="mt-1 text-sm text-red-600">
                            {passwordErrors.currentPassword.message}
                          </p>
                        )}
                      </div>

                      {/* New Password */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2 uppercase">
                          New Password<span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <input
                            type={showNewPassword ? 'text' : 'password'}
                            {...registerPassword('newPassword')}
                            disabled={isSubmittingPassword}
                            className={cn(
                              'w-full px-4 py-2.5 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-12',
                              passwordErrors.newPassword
                                ? 'border-red-500'
                                : 'border-gray-300',
                              isSubmittingPassword &&
                              'opacity-50 cursor-not-allowed'
                            )}
                          />
                          <button
                            type="button"
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            disabled={isSubmittingPassword}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 disabled:opacity-50"
                          >
                            {showNewPassword ? (
                              <EyeOff className="h-5 w-5" />
                            ) : (
                              <Eye className="h-5 w-5" />
                            )}
                          </button>
                        </div>
                        {passwordErrors.newPassword && (
                          <p className="mt-1 text-sm text-red-600">
                            {passwordErrors.newPassword.message}
                          </p>
                        )}
                      </div>

                      {/* Confirm Password */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2 uppercase">
                          Confirm Password<span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <input
                            type={showConfirmPassword ? 'text' : 'password'}
                            {...registerPassword('confirmPassword')}
                            disabled={isSubmittingPassword}
                            className={cn(
                              'w-full px-4 py-2.5 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-12',
                              passwordErrors.confirmPassword
                                ? 'border-red-500'
                                : 'border-gray-300',
                              isSubmittingPassword &&
                              'opacity-50 cursor-not-allowed'
                            )}
                          />
                          <button
                            type="button"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            disabled={isSubmittingPassword}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 disabled:opacity-50"
                          >
                            {showConfirmPassword ? (
                              <EyeOff className="h-5 w-5" />
                            ) : (
                              <Eye className="h-5 w-5" />
                            )}
                          </button>
                        </div>
                        {passwordErrors.confirmPassword && (
                          <p className="mt-1 text-sm text-red-600">
                            {passwordErrors.confirmPassword.message}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Submit Button */}
                    <div className="mt-8 pt-6 border-t border-gray-200">
                      <button
                        type="submit"
                        disabled={isSubmittingPassword}
                        className={cn(
                          'px-8 py-3 bg-[#1597ff] text-white font-semibold rounded-md transition-colors shadow-md',
                          isSubmittingPassword
                            ? 'opacity-50 cursor-not-allowed'
                            : 'hover:bg-[#0d7acc] hover:shadow-lg'
                        )}
                      >
                        {isSubmittingPassword ? (
                          <span className="flex items-center gap-2">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Changing...
                          </span>
                        ) : (
                          'Change Password'
                        )}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <ValidationModal
        {...applicantValidationModalProps}
        isOpen={showPasswordChangeDialog}
        title="Password Changed"
        message={
          isLoggingOut
            ? 'Logging out... Please wait while we redirect you.'
            : `Current Session Expired. You will be redirected to login in ${redirectCountdown} sec. Please log in again.`
        }
        variant="success"
        showValidationIcon={false}
        confirmText={isLoggingOut ? 'Redirecting...' : 'OK'}
        onClose={() => handlePasswordChangeLogout()}
      />

      {/* Error popup (replaces inline banner) */}
      <ConfirmDialog
        {...applicantConfirmDialogProps}
        isOpen={!!(error || localError)}
        title="Error"
        message={localError || error || ''}
        variant="error"
        primaryLabel="OK"
        onPrimary={() => {
          dispatch(clearError())
          setLocalError(null)
        }}
        secondaryLabel="Close"
        onSecondary={() => {
          dispatch(clearError())
          setLocalError(null)
        }}
      />
    </div>
  )
}
